import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  HostListener,
  inject,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  CdkDrag,
  CdkDragEnd,
  CdkDragDrop,
  CdkDragMove,
  CdkDragPreview,
  CdkDropList,
  CdkDropListGroup,
  moveItemInArray,
  transferArrayItem,
} from '@angular/cdk/drag-drop';
import { OrgChartDragStateService, ROOT_SENTINEL } from './org-chart-drag-state.service';
import {
  EconomicGroupMemberResponse,
  EconomicGroupTreeResponse,
} from '@interfaces/economic-groups/responses/economic-group.response';
import { MoveMemberRequest } from '@interfaces/economic-groups/requests/economic-group.request';
import { EconomicGroupsHttpService } from '@services/http/economic-groups/economic-groups-http.service';
import { CompanyService } from '@services/http/company.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { PermissionsService } from '@services/helpers/permissions.service';
import { VaultBaseComponent } from '@src/app/shared/components/base/vault-base.component';
import { SharedModule } from '@src/app/shared/shared.module';
import {
  GroupTreeNodeComponent,
  MemberMoveEvent,
} from '@src/app/features/dashboard/economic-groups/components/group-tree-node/group-tree-node.component';
import { QuickCreateCompanyModalComponent } from '@src/app/features/dashboard/economic-groups/components/modals/quick-create-company-modal/quick-create-company-modal.component';
import { SetPrimaryMemberModalComponent } from '@src/app/features/dashboard/economic-groups/components/modals/set-primary-member-modal/set-primary-member-modal.component';
import { OrgChartImportProgressCardComponent } from '@src/app/features/dashboard/economic-groups/components/org-chart-import-progress-card/org-chart-import-progress-card.component';
import { OrgChartImportReviewModalComponent } from '@src/app/features/dashboard/economic-groups/components/org-chart-import-review-modal/org-chart-import-review-modal.component';
import { OrgChartImportUploadModalComponent } from '@src/app/features/dashboard/economic-groups/components/org-chart-import-upload-modal/org-chart-import-upload-modal.component';
import { OrgChartImportStateService } from './org-chart-import-state.service';
import { TranslateService } from '@ngx-translate/core';
import { Company } from '@interfaces/common/company';
import { catchError, finalize, map } from 'rxjs/operators';
import { firstValueFrom, of } from 'rxjs';
import { AppState } from '@state/index';
import {
  selectSelectedEconomicGroupId,
  selectSelectedEconomicGroupName,
} from '@state/auth/auth.selectors';
import * as AuthActions from '@state/auth/auth.actions';
import { filterCompanyLibraryItems } from './company-library-filter.util';
import {
  collectMemberCompanyIds,
  countMembers,
  dedupeById,
  flattenMembers,
  removeMemberFromTree,
} from './org-chart-tree.util';

interface CompanyLibraryItem {
  id: string;
  name: string;
  cnpj: string;
  partners: string[];
}

@Component({
  selector: 'app-economic-group-builder',
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    CdkDrag,
    CdkDragPreview,
    CdkDropList,
    CdkDropListGroup,
    GroupTreeNodeComponent,
    QuickCreateCompanyModalComponent,
    SetPrimaryMemberModalComponent,
    OrgChartImportUploadModalComponent,
    OrgChartImportProgressCardComponent,
    OrgChartImportReviewModalComponent,
  ],
  providers: [OrgChartImportStateService],
  templateUrl: './economic-group-builder.component.html',
  styleUrls: ['./economic-group-builder.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EconomicGroupBuilderComponent extends VaultBaseComponent implements OnInit {
  @ViewChild('quickCreateModal') quickCreateModal?: QuickCreateCompanyModalComponent;
  @ViewChild('setPrimaryModal') setPrimaryModal?: SetPrimaryMemberModalComponent;
  @ViewChild('orgChartImportModal') orgChartImportModal?: OrgChartImportUploadModalComponent;

  /** Importação de organograma (card 3578): estado, tempo real e ações ficam no serviço. */
  readonly orgChartImport = inject(OrgChartImportStateService);

  readonly loading = signal(false);
  readonly companiesLoading = signal(false);
  readonly group = signal<EconomicGroupTreeResponse | null>(null);
  readonly isTenant = signal(false);
  readonly isPanning = signal(false);
  readonly isLibraryOpen = signal(true);
  readonly availableCompanies = signal<CompanyLibraryItem[]>([]);
  readonly companySearchQuery = signal('');
  readonly isEditingName = signal(false);
  readonly editingNameValue = signal('');
  readonly isSavingName = signal(false);
  readonly pendingMemberIds = signal<ReadonlySet<string>>(new Set());

  // Zoom & pan state
  private readonly zoomLevel = signal(1);
  private readonly panX = signal(0);
  private readonly panY = signal(0);

  readonly zoomPercent = computed(() => Math.round(this.zoomLevel() * 100));
  readonly canvasTransform = computed(() => {
    const z = this.zoomLevel();
    const px = this.panX();
    const py = this.panY();
    return `translate(calc(-50% + ${px}px), calc(-50% + ${py}px)) scale(${z})`;
  });

  /** Filtro apenas de exibição da biblioteca — não altera `availableCompanies`. */
  readonly isSearchingCompanies = computed(() => this.companySearchQuery().trim().length > 0);
  readonly filteredCompanies = computed(() =>
    filterCompanyLibraryItems(this.availableCompanies(), this.companySearchQuery())
  );

  readonly totalMemberCount = computed(() => {
    const tree = this.group();
    if (!tree) return 0;
    return (tree.primaryMember ? 1 : 0) + countMembers(tree.rootMembers);
  });

  private groupId!: string;
  private lastMouseX = 0;
  private lastMouseY = 0;
  private readonly removingMemberIds = new Set<string>();
  private readonly handledMemberDragEndIds = new Set<string>();
  private readonly handledLibraryDragEndCompanyIds = new Set<string>();
  private readonly movingLibraryCompanyIds = new Set<string>();
  private readonly optimisticMemberCompanyIds = new Map<string, string>();

  readonly acceptOnlyGroupMembers = (drag: CdkDrag): boolean => this.isGroupMember(drag.data);
  readonly isPendingMember = (memberId: string): boolean => this.pendingMemberIds().has(memberId);

  constructor(
    translateService: TranslateService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly groupsService: EconomicGroupsHttpService,
    private readonly companyService: CompanyService,
    private readonly toast: ToastHelperService,
    private readonly permissions: PermissionsService,
    private readonly cdr: ChangeDetectorRef,
    private readonly destroyRef: DestroyRef,
    private readonly authStore: Store<AppState>,
    readonly dragState: OrgChartDragStateService
  ) {
    super(translateService);
  }

  ngOnInit(): void {
    this.groupId = this.route.snapshot.paramMap.get('id') ?? '';
    if (!this.groupId) {
      this.router.navigate(['/dashboard/economic-groups']);
      return;
    }

    this.permissions
      .hasRole('TENANT')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(is => this.isTenant.set(is));

    this.loadTree();
    this.orgChartImport.init(this.groupId);
    this.orgChartImport.treeChanged$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.reconcileTreeSilently());
  }

  @HostListener('document:pointerup')
  onDocumentPointerUp(): void {
    globalThis.setTimeout(() => {
      if (typeof document === 'undefined') return;
      if (document.querySelector('.cdk-drag-preview')) return;
      this.dragState.clear();
      this.cdr.markForCheck();
    });
  }

  loadTree(): void {
    this.loading.set(true);
    this.groupsService
      .getTree(this.groupId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: tree => {
          if (tree) this.group.set(tree);
          this.loading.set(false);
          this.loadAvailableCompanies();
          this.cdr.markForCheck();
        },
        error: () => {
          this.toast.showToastError('Erro ao carregar o grupo econômico.');
          this.loading.set(false);
          this.cdr.markForCheck();
        },
      });
  }

  goBack(): void {
    this.router.navigate(['/dashboard/economic-groups']);
  }

  startEditingName(): void {
    this.editingNameValue.set(this.group()!.name);
    this.isEditingName.set(true);
    setTimeout(() => {
      const el = document.querySelector<HTMLInputElement>('.builder-topbar__name-input');
      el?.focus();
      el?.select();
    });
  }

  confirmNameEdit(): void {
    if (!this.isEditingName()) return;
    const name = this.editingNameValue().trim();
    this.isEditingName.set(false);
    if (!name || name === this.group()!.name) return;
    if (this.isSavingName()) return;
    this.isSavingName.set(true);

    const currentGroup = this.group()!;
    this.groupsService
      .update(this.groupId, { name, description: currentGroup.description })
      .pipe(
        finalize(() => this.isSavingName.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.group.update(g => (g ? { ...g, name } : g));
          this.cdr.markForCheck();
        },
        error: () => {
          this.toast.showToastError('Erro ao salvar o nome do grupo.');
          this.cdr.markForCheck();
        },
      });
  }

  cancelNameEdit(): void {
    this.isEditingName.set(false);
  }

  openOrgChartImport(): void {
    this.orgChartImportModal?.open();
  }

  openQuickCreate(): void {
    this.quickCreateModal?.open(this.groupId);
  }

  onRootCardClick(): void {
    this.setPrimaryModal?.open(this.groupId, this.getAllMemberCompanyIds());
  }

  onPrimaryMemberRemoved(event: MouseEvent): void {
    event.stopPropagation();
    const previousState = this.snapshotTree(this.group());
    if (!previousState) return;

    this.groupsService
      .removePrimaryMember(this.groupId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          const updated = { ...previousState };
          const removedMember = previousState.primaryMember;
          updated.primaryMember = undefined;
          if (removedMember) {
            updated.rootMembers = [...previousState.rootMembers, removedMember];
          }
          this.group.set(updated);
          this.cdr.markForCheck();
        },
        error: () => {
          this.toast.showToastError('Erro ao remover empresa principal.');
          this.cdr.markForCheck();
        },
      });
  }

  // ── Zoom & pan ────────────────────────────────────────────────────────────────

  zoomIn(): void {
    this.zoomLevel.update(z => Math.min(+(z + 0.1).toFixed(1), 2));
  }

  zoomOut(): void {
    this.zoomLevel.update(z => Math.max(+(z - 0.1).toFixed(1), 0.3));
  }

  fitView(): void {
    this.zoomLevel.set(1);
    this.panX.set(0);
    this.panY.set(0);
  }

  toggleLibrary(): void {
    const isClosing = this.isLibraryOpen();
    // Fechar o painel zera a busca para ele reabrir mostrando a lista completa.
    if (isClosing) this.clearCompanySearch();
    this.isLibraryOpen.set(!isClosing);
  }

  onCompanySearch(event: Event): void {
    this.companySearchQuery.set((event.target as HTMLInputElement).value);
  }

  clearCompanySearch(): void {
    this.companySearchQuery.set('');
  }

  startPan(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (
      target.closest('.org-card') ||
      target.closest('.orgchart__root-card') ||
      target.closest('.zoom-controls')
    )
      return;
    this.isPanning.set(true);
    this.lastMouseX = event.clientX;
    this.lastMouseY = event.clientY;
  }

  doPan(event: MouseEvent): void {
    if (!this.isPanning()) return;
    const dx = event.clientX - this.lastMouseX;
    const dy = event.clientY - this.lastMouseY;
    this.panX.update(x => x + dx);
    this.panY.update(y => y + dy);
    this.lastMouseX = event.clientX;
    this.lastMouseY = event.clientY;
  }

  endPan(): void {
    this.isPanning.set(false);
  }

  onWheel(event: WheelEvent): void {
    const delta = event.deltaY > 0 ? -0.1 : 0.1;
    this.zoomLevel.update(z => Math.min(Math.max(+(z + delta).toFixed(1), 0.3), 2));
  }

  // ── Drag-drop ─────────────────────────────────────────────────────────────────

  onRootDragMoved(event: CdkDragMove<EconomicGroupMemberResponse>): void {
    this.dragState.updateFromCursor(
      event.pointerPosition.x,
      event.pointerPosition.y,
      event.source.data.id
    );
  }

  onRootMemberDragEnded(event: CdkDragEnd<EconomicGroupMemberResponse>): void {
    if (this.isOverCompanyLibrary(event.dropPoint.x, event.dropPoint.y)) {
      this.handledMemberDragEndIds.add(event.source.data.id);
      this.dragState.clear();
      this.removeMemberToLibrary(event.source.data);
      return;
    }

    const previousState = this.snapshotTree(this.group());
    const nestTargetId = this.dragState.nestTargetId();
    this.dragState.clear();
    if (!previousState || !nestTargetId) return;

    const dragged = event.source.data;
    this.handledMemberDragEndIds.add(dragged.id);

    if (nestTargetId === ROOT_SENTINEL) {
      return;
    }

    if (this.isPendingMember(nestTargetId)) return;
    if (dragged.id === nestTargetId) return;

    const allMembers = flattenMembers(previousState.rootMembers);
    const target = allMembers.find(m => m.id === nestTargetId);
    this.applyMoveOptimistic(dragged.id, nestTargetId, target?.children?.length ?? 0);
    this.persistMove(dragged.id, nestTargetId, target?.children?.length ?? 0, previousState);
  }

  shouldIgnoreMemberDrop(memberId: string): boolean {
    if (!this.handledMemberDragEndIds.has(memberId)) return false;
    this.handledMemberDragEndIds.delete(memberId);
    return true;
  }

  onRootDrop(event: CdkDragDrop<EconomicGroupMemberResponse[] | CompanyLibraryItem[]>): void {
    const currentState = this.group();
    const previousState = this.snapshotTree(currentState);
    if (!currentState || !previousState) return;

    if (this.isGroupMember(event.item.data) && this.shouldIgnoreMemberDrop(event.item.data.id)) {
      this.dragState.clear();
      return;
    }

    const nestTargetId = this.dragState.nestTargetId();

    if (this.isCompanyLibraryItem(event.item.data)) {
      if (this.shouldIgnoreLibraryDrop(event.item.data.id)) {
        this.dragState.clear();
        return;
      }
      this.dropLibraryCompanyAtPointer(event.item.data, event.dropPoint, true);
      this.dragState.clear();
      return;
    }

    this.dragState.clear(); // always clear after reading
    if (nestTargetId) {
      const dragged: EconomicGroupMemberResponse = event.item.data;
      if (nestTargetId === ROOT_SENTINEL) {
        // Drop on root card — member is already at root level, nothing to do
        return;
      }
      if (this.isPendingMember(nestTargetId)) return;
      if (dragged.id === nestTargetId) return;
      const allMembers = flattenMembers(currentState.rootMembers);
      const target = allMembers.find(m => m.id === nestTargetId);
      this.applyMoveOptimistic(dragged.id, nestTargetId, target?.children?.length ?? 0);
      this.persistMove(dragged.id, nestTargetId, target?.children?.length ?? 0, previousState);
      return;
    }

    if (event.previousContainer === event.container && event.previousIndex === event.currentIndex) {
      return;
    }

    const member = event.item.data as EconomicGroupMemberResponse;
    const previousContainerData = event.previousContainer.data as EconomicGroupMemberResponse[];
    const containerData = event.container.data as EconomicGroupMemberResponse[];

    if (event.previousContainer === event.container) {
      moveItemInArray(containerData, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(
        previousContainerData,
        containerData,
        event.previousIndex,
        event.currentIndex
      );
    }

    this.group.set({ ...currentState });
    this.cdr.markForCheck();
    this.persistMove(member.id, null, event.currentIndex, previousState);
  }

  onMemberMoved(event: MemberMoveEvent): void {
    const previousState = this.snapshotTree(this.group());
    if (!previousState) return;
    if (event.newParentMemberId && this.isPendingMember(event.newParentMemberId)) return;
    // When moving to root (null parent), append at end of root members
    const orderIndex =
      event.newParentMemberId === null ? previousState.rootMembers.length : event.orderIndex;
    this.applyMoveOptimistic(event.memberId, event.newParentMemberId, orderIndex);
    this.persistMove(event.memberId, event.newParentMemberId, orderIndex, previousState);
  }

  onMemberAdded(): void {
    this.loadTree();
  }

  onCompanyLibraryDrop(
    event: CdkDragDrop<CompanyLibraryItem[] | EconomicGroupMemberResponse[]>
  ): void {
    if (this.isGroupMember(event.item.data)) {
      this.removeMemberToLibrary(event.item.data);
      return;
    }
    if (this.isCompanyLibraryItem(event.item.data)) {
      if (this.shouldIgnoreLibraryDrop(event.item.data.id)) {
        this.dragState.clear();
        return;
      }
      const handled = this.dropLibraryCompanyAtPointer(event.item.data, event.dropPoint, false);
      this.dragState.clear();
      if (!handled) return;
    }
  }

  onLibraryCompanyDragMoved(event: CdkDragMove<CompanyLibraryItem>): void {
    this.dragState.updateFromCursorOnNextFrame(
      event.pointerPosition.x,
      event.pointerPosition.y,
      event.source.data.id
    );
  }

  onLibraryCompanyDragEnded(event: CdkDragEnd<CompanyLibraryItem>): void {
    if (this.isOverCompanyLibrary(event.dropPoint.x, event.dropPoint.y)) {
      this.dragState.clear();
      return;
    }

    const tree = this.group();
    if (!tree) {
      this.dragState.clear();
      return;
    }

    const handled = this.dropLibraryCompanyAtPointer(event.source.data, event.dropPoint, false);
    if (!handled) {
      this.dragState.clear();
      return;
    }

    this.handledLibraryDragEndCompanyIds.add(event.source.data.id);
    this.dragState.clear();
  }

  onMemberDroppedInLibrary(member: EconomicGroupMemberResponse): void {
    this.removeMemberToLibrary(member);
  }

  // ── Private helpers ───────────────────────────────────────────────────────────

  private persistMove(
    memberId: string,
    newParentMemberId: string | null,
    orderIndex: number,
    previousState: EconomicGroupTreeResponse
  ): void {
    const payload: MoveMemberRequest = {
      memberId: this.optimisticMemberCompanyIds.has(memberId) ? undefined : memberId,
      companyId: this.optimisticMemberCompanyIds.get(memberId),
      newParentMemberId: newParentMemberId ?? undefined,
      orderIndex,
    };

    this.groupsService
      .moveMember(this.groupId, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {},
        error: () => {
          this.group.set(previousState);
          this.toast.showToastError('Erro ao mover empresa. A posição foi revertida.');
          this.cdr.markForCheck();
        },
      });
  }

  private moveCompanyFromLibrary(
    company: CompanyLibraryItem,
    parentMemberId: string | null,
    orderIndex: number
  ): void {
    if (this.movingLibraryCompanyIds.has(company.id)) return;
    this.movingLibraryCompanyIds.add(company.id);

    const previousState = this.snapshotTree(this.group());
    const previousCompanies = this.availableCompanies();
    const optimisticMember = this.createOptimisticMember(company, parentMemberId, orderIndex);

    this.availableCompanies.update(list => list.filter(c => c.id !== company.id));
    this.addPendingMember(optimisticMember.id, company.id);
    this.insertMemberOptimistic(optimisticMember, parentMemberId, orderIndex);

    this.groupsService
      .moveMember(this.groupId, {
        companyId: company.id,
        newParentMemberId: parentMemberId ?? undefined,
        orderIndex,
      })
      .pipe(finalize(() => this.movingLibraryCompanyIds.delete(company.id)))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.removePendingMember(optimisticMember.id);
          this.reconcileTreeSilently();
        },
        error: () => {
          if (previousState) this.group.set(previousState);
          this.availableCompanies.set(previousCompanies);
          this.removePendingMember(optimisticMember.id);
          this.toast.showToastError('Erro ao adicionar empresa ao organograma.');
          this.cdr.markForCheck();
        },
      });
  }

  private shouldIgnoreLibraryDrop(companyId: string): boolean {
    if (!this.handledLibraryDragEndCompanyIds.has(companyId)) return false;
    this.handledLibraryDragEndCompanyIds.delete(companyId);
    return true;
  }

  private dropLibraryCompanyAtPointer(
    company: CompanyLibraryItem,
    dropPoint: { x: number; y: number },
    useRootAsFallback: boolean
  ): boolean {
    if (this.isOverCompanyLibrary(dropPoint.x, dropPoint.y)) return false;

    const tree = this.group();
    if (!tree) return false;

    const targetId =
      this.dragState.nestTargetId() ??
      this.dragState.getTargetIdFromCursor(dropPoint.x, dropPoint.y, company.id);

    if (!targetId && !useRootAsFallback) return false;

    const parentMemberId = targetId && targetId !== ROOT_SENTINEL ? targetId : null;
    if (parentMemberId && this.isPendingMember(parentMemberId)) return false;
    const parent = parentMemberId
      ? flattenMembers(tree.rootMembers).find(m => m.id === parentMemberId)
      : null;

    this.moveCompanyFromLibrary(
      company,
      parentMemberId,
      parent?.children?.length ?? tree.rootMembers.length
    );
    return true;
  }

  private removeMemberToLibrary(member: EconomicGroupMemberResponse): void {
    if (this.removingMemberIds.has(member.id)) return;
    this.removingMemberIds.add(member.id);

    const previousState = this.snapshotTree(this.group());
    const previousCompanies = this.availableCompanies();
    const removedMembers = flattenMembers([member]);
    this.removeMemberOptimistic(member.id);
    this.availableCompanies.update(list => this.mergeCompaniesIntoLibrary(list, removedMembers));

    this.groupsService
      .removeMember(this.groupId, member.id)
      .pipe(
        finalize(() => this.removingMemberIds.delete(member.id)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.reconcileTreeSilently();
        },
        error: () => {
          if (previousState) this.group.set(previousState);
          this.availableCompanies.set(previousCompanies);
          this.toast.showToastError('Erro ao remover empresa do organograma.');
          this.cdr.markForCheck();
        },
      });
  }

  private loadAvailableCompanies(showLoading = true, showError = true): void {
    const existingMemberIds = this.getAllMemberCompanyIds();
    if (showLoading) this.companiesLoading.set(true);
    this.companyService
      .getAllCompanys()
      .pipe(
        map(res =>
          (res?.data ?? [])
            .filter((company): company is Company & { id: string } => {
              return !!company.id && !existingMemberIds.includes(company.id);
            })
            .map(company => this.toCompanyLibraryItem(company))
        ),
        catchError(() => {
          if (showError) this.toast.showToastError('Erro ao carregar biblioteca de empresas.');
          return of(showError ? [] : this.availableCompanies());
        }),
        finalize(() => {
          if (showLoading) this.companiesLoading.set(false);
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(companies => this.availableCompanies.set(dedupeById(companies)));
  }

  private getAllMemberCompanyIds(): string[] {
    const tree = this.group();
    if (!tree) return [];
    const ids = collectMemberCompanyIds(tree.rootMembers);
    if (tree.primaryMember) ids.push(tree.primaryMember.companyId);
    return ids;
  }

  private toCompanyLibraryItem(company: Company & { id: string }): CompanyLibraryItem {
    return {
      id: company.id,
      name: company.corporateName || company.displayName || company.tradeName || 'Empresa sem nome',
      cnpj: company.cnpj,
      partners: (company.partners ?? []).map(partner => partner.name).filter(Boolean),
    };
  }

  private toCompanyLibraryItemFromMember(member: EconomicGroupMemberResponse): CompanyLibraryItem {
    return {
      id: member.companyId,
      name: member.companyName,
      cnpj: member.companyCnpj,
      partners: member.partners ?? [],
    };
  }

  private isCompanyLibraryItem(value: unknown): value is CompanyLibraryItem {
    const item = value as CompanyLibraryItem;
    return !!item?.id && typeof item.name === 'string' && !this.isGroupMember(value);
  }

  private isGroupMember(value: unknown): value is EconomicGroupMemberResponse {
    const item = value as EconomicGroupMemberResponse;
    return !!item?.id && typeof item.companyId === 'string';
  }

  private isOverCompanyLibrary(cursorX: number, cursorY: number): boolean {
    return this.dragState
      .getElementsFromPoint(cursorX, cursorY)
      .some(element => !!element.closest('[data-company-library]'));
  }

  private applyMoveOptimistic(
    memberId: string,
    newParentMemberId: string | null,
    orderIndex: number
  ): void {
    const tree = this.group();
    if (!tree) return;

    const allMembers = flattenMembers(tree.rootMembers);
    const member = allMembers.find(m => m.id === memberId);
    if (!member) return;

    removeMemberFromTree(tree.rootMembers, memberId);
    member.parentMemberId = newParentMemberId ?? undefined;
    member.orderIndex = orderIndex;

    if (!newParentMemberId) {
      tree.rootMembers.splice(orderIndex, 0, member);
    } else {
      const newParent = allMembers.find(m => m.id === newParentMemberId);
      if (newParent) {
        newParent.children = newParent.children ?? [];
        newParent.children.splice(orderIndex, 0, member);
      }
    }

    this.group.set({ ...tree });
    this.cdr.markForCheck();
  }

  private insertMemberOptimistic(
    member: EconomicGroupMemberResponse,
    parentMemberId: string | null,
    orderIndex: number
  ): void {
    const tree = this.group();
    if (!tree) return;

    if (!parentMemberId) {
      tree.rootMembers.splice(orderIndex, 0, member);
    } else {
      const parent = flattenMembers(tree.rootMembers).find(m => m.id === parentMemberId);
      if (!parent) return;
      parent.children = parent.children ?? [];
      parent.children.splice(orderIndex, 0, member);
    }

    this.group.set({ ...tree });
    this.cdr.markForCheck();
  }

  private removeMemberOptimistic(memberId: string): void {
    const tree = this.group();
    if (!tree) return;
    removeMemberFromTree(tree.rootMembers, memberId);
    this.group.set({ ...tree });
    this.cdr.markForCheck();
  }

  private reconcileTreeSilently(): void {
    this.groupsService
      .getTree(this.groupId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: tree => {
          if (tree) {
            this.group.set(tree);
            this.optimisticMemberCompanyIds.clear();
            this.pendingMemberIds.set(new Set());
            void this.syncSelectedEconomicGroupScope(tree);
            this.loadAvailableCompanies(false, false);
          }
          this.cdr.markForCheck();
        },
        error: () => {
          this.cdr.markForCheck();
        },
      });
  }

  private createOptimisticMember(
    company: CompanyLibraryItem,
    parentMemberId: string | null,
    orderIndex: number
  ): EconomicGroupMemberResponse {
    return {
      id: `optimistic-${company.id}-${Date.now()}`,
      economicGroupId: this.groupId,
      companyId: company.id,
      companyName: company.name,
      companyCnpj: company.cnpj,
      parentMemberId: parentMemberId ?? undefined,
      orderIndex,
      partners: company.partners,
      children: [],
    };
  }

  private mergeCompaniesIntoLibrary(
    currentCompanies: CompanyLibraryItem[],
    members: EconomicGroupMemberResponse[]
  ): CompanyLibraryItem[] {
    const existingIds = new Set<string>();
    const uniqueCurrentCompanies = currentCompanies.filter(company => {
      if (existingIds.has(company.id)) return false;
      existingIds.add(company.id);
      return true;
    });
    const returnedCompanies = members
      .map(member => this.toCompanyLibraryItemFromMember(member))
      .filter(company => {
        if (existingIds.has(company.id)) return false;
        existingIds.add(company.id);
        return true;
      });

    return [...uniqueCurrentCompanies, ...returnedCompanies];
  }

  private addPendingMember(memberId: string, companyId: string): void {
    this.optimisticMemberCompanyIds.set(memberId, companyId);
    this.pendingMemberIds.update(ids => new Set(ids).add(memberId));
  }

  private removePendingMember(memberId: string): void {
    this.optimisticMemberCompanyIds.delete(memberId);
    this.pendingMemberIds.update(ids => {
      const updated = new Set(ids);
      updated.delete(memberId);
      return updated;
    });
  }

  private snapshotTree(tree: EconomicGroupTreeResponse | null): EconomicGroupTreeResponse | null {
    return tree ? structuredClone(tree) : null;
  }

  private async syncSelectedEconomicGroupScope(tree: EconomicGroupTreeResponse): Promise<void> {
    const selectedGroupId = await firstValueFrom(
      this.authStore.select(selectSelectedEconomicGroupId)
    );
    if (selectedGroupId !== tree.id) return;

    const companyIds = this.getMemberCompanyIdsFromTree(tree);
    if (companyIds.length === 0) {
      this.authStore.dispatch(AuthActions.clearUserLoggedCustomCompanies());
      this.authStore.dispatch(AuthActions.clearSelectedEconomicGroup());
      return;
    }

    this.authStore.dispatch(AuthActions.setUserLoggedCustomCompanies({ companyIds }));

    const selectedGroupName = await firstValueFrom(
      this.authStore.select(selectSelectedEconomicGroupName)
    );
    if (selectedGroupName !== tree.name) {
      this.authStore.dispatch(
        AuthActions.setSelectedEconomicGroup({ id: tree.id, name: tree.name })
      );
    }
  }

  private getMemberCompanyIdsFromTree(tree: EconomicGroupTreeResponse): string[] {
    const members = [...(tree.primaryMember ? [tree.primaryMember] : []), ...tree.rootMembers];
    return [...new Set(collectMemberCompanyIds(members))];
  }
}
